/**
 * Column width state.
 *
 * Widths live in the core store rather than in each renderer so that resizing
 * behaves identically in React and Angular — the same clamping, the same reset
 * semantics — instead of being implemented twice from the same description.
 *
 * A width is a pixel number. A column with no entry here falls back to whatever
 * track its `ColumnDef` declares, which is how a flexible `1fr` name column
 * stays flexible until someone actually drags it.
 */
export type ColumnWidths = Readonly<Record<string, number>>;

/** Below this a column has no room for a label, so dragging stops here. */
export const MIN_COLUMN_WIDTH = 56;

export interface ColumnWidthBounds {
  min?: number;
  max?: number;
}

/**
 * The default track for a column that has not declared a width.
 *
 * The floor matters: a bare `minmax(0, 1fr)` lets a flexible column be squeezed
 * to zero when its neighbours are widened, which silently hides the file names.
 */
export const FLEX_TRACK = `minmax(${MIN_COLUMN_WIDTH}px, 1fr)`;

/** A flexible track that will not shrink past `min`. */
export function flexTrack(min?: number): string {
  return `minmax(${Math.max(min ?? MIN_COLUMN_WIDTH, MIN_COLUMN_WIDTH)}px, 1fr)`;
}

/** Clamp one width into its allowed range. */
export function clampColumnWidth(width: number, bounds: ColumnWidthBounds = {}): number {
  const min = Math.max(bounds.min ?? MIN_COLUMN_WIDTH, MIN_COLUMN_WIDTH);
  const max = bounds.max ?? Number.POSITIVE_INFINITY;
  if (!Number.isFinite(width)) return min;
  return Math.round(Math.min(Math.max(width, min), max));
}

/** Set one column's width, returning a new map. */
export function setColumnWidth(
  widths: ColumnWidths,
  key: string,
  width: number,
  bounds?: ColumnWidthBounds,
): ColumnWidths {
  const next = clampColumnWidth(width, bounds);
  if (widths[key] === next) return widths;
  return { ...widths, [key]: next };
}

/**
 * Drop a column's override so it returns to its declared track. This is what a
 * double-click on the resize handle does.
 */
export function resetColumnWidth(widths: ColumnWidths, key: string): ColumnWidths {
  if (!(key in widths)) return widths;
  const next = { ...widths };
  delete next[key];
  return next;
}

/** Pull the pixel floor out of a declared track, if it has one. */
function trackFloor(track: string): number {
  const fixed = /^(\d+(?:\.\d+)?)px$/.exec(track);
  if (fixed) return Number(fixed[1]);
  const flexible = /^minmax\(\s*(\d+(?:\.\d+)?)px/.exec(track);
  if (flexible) return Number(flexible[1]);
  // A percentage or other relative track contributes nothing we can rely on.
  return 0;
}

export interface GridMinWidthInput {
  key: string;
  width?: string;
  minWidth?: number;
}

/**
 * The narrowest the row can be while still honouring every track.
 *
 * Without this the row element stays at container width while its tracks
 * overflow, so scrolling right reveals cells sitting outside their own row —
 * the hover highlight, selection background and bottom border all stop short.
 *
 * Returned as a `calc()` so the gap and padding stay tied to `--de-spacing`
 * rather than being hard-coded here.
 */
export function gridMinWidth(
  columns: readonly GridMinWidthInput[],
  hasCheckbox: boolean,
  hasActions: boolean,
  widths: ColumnWidths = {},
): string {
  let pixels = 0;
  let tracks = 0;

  if (hasCheckbox) {
    pixels += 16;
    tracks += 1;
  }
  for (const column of columns) {
    pixels += trackFloor(trackFor(column.key, column.width, widths, column.minWidth));
    tracks += 1;
  }
  if (hasActions) {
    pixels += 32;
    tracks += 1;
  }

  const gaps = Math.max(0, tracks - 1);
  // Row padding is `0 calc(var(--de-spacing) * 2)`, so four units in total.
  return `calc(${pixels}px + ${gaps} * var(--de-spacing) * 2 + var(--de-spacing) * 4)`;
}

/**
 * Resolve a column to a CSS grid track.
 *
 * A dragged column becomes a fixed pixel track; everything else keeps whatever
 * it declared, defaulting to a flexible track.
 */
export function trackFor(
  key: string,
  declaredWidth: string | undefined,
  widths: ColumnWidths,
  minWidth?: number,
): string {
  const override = widths[key];
  if (override !== undefined) return `${override}px`;
  if (declaredWidth === undefined) return flexTrack(minWidth);
  // Any flexible track is rebuilt from the column's floor, so `minWidth` is
  // honoured and widening one column can never squeeze another out of
  // existence — a bare `minmax(0, 1fr)` would allow exactly that.
  if (/^minmax\(.*1fr\s*\)$/.test(declaredWidth.trim())) return flexTrack(minWidth);
  return declaredWidth;
}
